import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { User, Shield, Server, Mail, Send, Save, Calendar, FileUp, Download, Database, Archive, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

const empty = {
  enabled: true,
  recipient: "lager@celentano-garagentore.de",
  provider: "resend",
  resend_api_key: "",
  sender: "onboarding@resend.dev",
  smtp_host: "",
  smtp_port: 587,
  smtp_user: "",
  smtp_password: "",
  smtp_starttls: true,
  resend_api_key_set: false,
  smtp_password_set: false,
};

export default function Settings() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/email");
      setCfg({ ...empty, ...data });
    } catch (e) {
      toast.error("Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setCfg((s) => ({ ...s, [k]: v }));
  };

  const setNum = (k) => (e) =>
    setCfg((s) => ({ ...s, [k]: Number(e.target.value) || 0 }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      delete payload.resend_api_key_set;
      delete payload.smtp_password_set;
      const { data } = await api.put("/settings/email", payload);
      setCfg({ ...empty, ...data });
      toast.success("E-Mail-Einstellungen gespeichert.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/email/test", {});
      toast.success(`Test-E-Mail an ${data.to} gesendet.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const sendDigestNow = async () => {
    try {
      await api.post("/settings/email/digest-now");
      toast.success("Wochen-Zusammenfassung wird im Hintergrund versendet.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const { data } = await api.post("/articles/import", { csv: text, mode: "merge" });
      setImportResult(data);
      toast.success(`Import: +${data.created} neu, ${data.updated} aktualisiert, ${data.skipped} übersprungen.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("lager_token");
    fetch(`${API}/articles.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `artikel_${Date.now()}.csv`;
        a.click();
      });
  };

  // ---------- Generic download via authenticated fetch ----------
  const downloadFile = async (path, fallbackName) => {
    const token = localStorage.getItem("lager_token");
    try {
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      let name = fallbackName;
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m) name = m[1];
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
    } catch (err) {
      toast.error(`Export fehlgeschlagen: ${err.message}`);
    }
  };

  // ---------- Backup ZIP Import ----------
  const [backupImporting, setBackupImporting] = useState(false);
  const [backupResult, setBackupResult] = useState(null);
  const [backupMode, setBackupMode] = useState("replace");

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const idx = result.indexOf(",");
        resolve(idx >= 0 ? result.slice(idx + 1) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onBackupImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (backupMode === "replace") {
      const ok = window.confirm(
        "ACHTUNG: Modus 'Komplett ersetzen' löscht alle aktuellen Daten und ersetzt sie durch das Backup. Fortfahren?",
      );
      if (!ok) {
        e.target.value = "";
        return;
      }
    }
    setBackupImporting(true);
    setBackupResult(null);
    try {
      const file_base64 = await fileToBase64(file);
      const { data } = await api.post("/import/backup", { file_base64, mode: backupMode });
      setBackupResult(data);
      toast.success("Backup importiert.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBackupImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-[900px] mx-auto space-y-6" data-testid="settings-page">
      <div>
        <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">SYSTEM · KONTO</div>
        <h1 className="font-display text-4xl uppercase">Einstellungen</h1>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6" style={{ borderLeft: "4px solid #F97316" }}>
        <div className="flex items-center gap-3 mb-4">
          <User className="w-5 h-5" />
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">BENUTZER</div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex">
            <span className="w-32 text-zinc-500">Name</span>
            <span className="font-medium">{user?.name}</span>
          </div>
          <div className="flex">
            <span className="w-32 text-zinc-500">E-Mail</span>
            <span className="font-mono-num">{user?.email}</span>
          </div>
          <div className="flex">
            <span className="w-32 text-zinc-500">Rolle</span>
            <span className="font-medium uppercase">{user?.role}</span>
          </div>
        </div>
      </div>

      {/* Email settings */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6" style={{ borderLeft: "4px solid #09090B" }}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5" />
            <div>
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">
                BENACHRICHTIGUNGEN
              </div>
              <div className="font-display text-lg uppercase mt-0.5">E-Mail bei niedrigem Bestand</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="email-enabled" className="text-xs uppercase tracking-wider font-semibold">
              Aktiv
            </Label>
            <Switch
              id="email-enabled"
              checked={!!cfg.enabled}
              onCheckedChange={(v) => setCfg((s) => ({ ...s, enabled: !!v }))}
              data-testid="email-enabled-switch"
            />
          </div>
        </div>

        {loading ? (
          <div className="mono text-xs text-zinc-500">LADEN…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">
                  Empfänger-E-Mail
                </Label>
                <Input
                  value={cfg.recipient || ""}
                  onChange={set("recipient")}
                  data-testid="email-recipient-input"
                  placeholder="lager@celentano-garagentore.de"
                  className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  Diese Adresse erhält Niedrigbestand-Meldungen und Test-Mails.
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Provider</Label>
                <Select value={cfg.provider} onValueChange={(v) => setCfg((s) => ({ ...s, provider: v }))}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="email-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm">
                    <SelectItem value="resend">Resend (API)</SelectItem>
                    <SelectItem value="smtp">SMTP-Server</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">
                  Absender (FROM)
                </Label>
                <Input
                  value={cfg.sender || ""}
                  onChange={set("sender")}
                  data-testid="email-sender-input"
                  placeholder="noreply@deine-domain.de"
                  className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                />
              </div>
            </div>

            {cfg.provider === "resend" && (
              <div className="mt-5 pt-5 border-t border-zinc-200">
                <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3">
                  RESEND
                </div>
                <Label className="text-xs uppercase tracking-wider font-semibold">API-Key</Label>
                <Input
                  type="password"
                  value={cfg.resend_api_key || ""}
                  onChange={set("resend_api_key")}
                  data-testid="resend-key-input"
                  placeholder={cfg.resend_api_key_set ? "•••••••• (gespeichert — leer lassen zum Beibehalten)" : "re_..."}
                  className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  Erhältst du unter resend.com → API Keys. Leer lassen, um den gespeicherten Wert beizubehalten.
                </div>
              </div>
            )}

            {cfg.provider === "smtp" && (
              <div className="mt-5 pt-5 border-t border-zinc-200">
                <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3">
                  SMTP-SERVER
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold">Host</Label>
                    <Input
                      value={cfg.smtp_host || ""}
                      onChange={set("smtp_host")}
                      data-testid="smtp-host-input"
                      placeholder="mail.celentano-garagentore.de"
                      className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Port</Label>
                    <Input
                      type="number"
                      value={cfg.smtp_port || 587}
                      onChange={setNum("smtp_port")}
                      data-testid="smtp-port-input"
                      className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Benutzer</Label>
                    <Input
                      value={cfg.smtp_user || ""}
                      onChange={set("smtp_user")}
                      data-testid="smtp-user-input"
                      placeholder="benutzer@..."
                      className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Passwort</Label>
                    <Input
                      type="password"
                      value={cfg.smtp_password || ""}
                      onChange={set("smtp_password")}
                      data-testid="smtp-pass-input"
                      placeholder={cfg.smtp_password_set ? "•••••••• (gespeichert)" : "••••••••"}
                      className="mt-1.5 rounded-sm border-zinc-400 font-mono-num"
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="smtp-tls"
                        checked={!!cfg.smtp_starttls}
                        onCheckedChange={(v) => setCfg((s) => ({ ...s, smtp_starttls: !!v }))}
                        data-testid="smtp-tls-switch"
                      />
                      <Label htmlFor="smtp-tls" className="text-xs uppercase tracking-wider font-semibold">
                        STARTTLS
                      </Label>
                    </div>
                  </div>
                </div>
                <div className="text-[11px] text-zinc-500 mt-2">
                  Port 465 = implizites SSL (STARTTLS irrelevant). Port 587 = STARTTLS empfohlen.
                </div>
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-zinc-200">
              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3">
                AUTO-NACHBESTELLUNG
              </div>
              <div className="flex items-center justify-between p-3 border border-zinc-200 rounded-sm bg-zinc-50 mb-3">
                <div>
                  <div className="text-sm font-medium">Automatische Bestellung bei Mindestmenge</div>
                  <div className="text-[11px] text-zinc-500">
                    Wenn ein Artikel die Mindestmenge erreicht und ein Lieferant hinterlegt ist, wird eine PDF-Bestellung
                    automatisch an den Lieferanten gesendet.
                  </div>
                </div>
                <Switch
                  checked={!!cfg.auto_reorder}
                  onCheckedChange={(v) => setCfg((s) => ({ ...s, auto_reorder: !!v }))}
                  data-testid="auto-reorder-switch"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Firmenname (PDF/E-Mail)</Label>
                  <Input
                    value={cfg.company_name || ""}
                    onChange={set("company_name")}
                    data-testid="company-name-input"
                    className="mt-1.5 rounded-sm border-zinc-400"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Firmen-Adresse (PDF)</Label>
                  <Textarea
                    value={cfg.company_address || ""}
                    onChange={set("company_address")}
                    placeholder="Straße Hausnr.&#10;PLZ Ort"
                    data-testid="company-address-input"
                    className="mt-1.5 rounded-sm border-zinc-400"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button
                onClick={save}
                disabled={saving}
                data-testid="save-email-settings"
                className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11 px-5 font-semibold tracking-wide"
              >
                <Save className="w-4 h-4 mr-2" /> {saving ? "SPEICHERN…" : "SPEICHERN"}
              </Button>
              <Button
                onClick={sendTest}
                disabled={testing}
                data-testid="send-test-email"
                variant="outline"
                className="rounded-sm border-zinc-400 h-11 px-5"
              >
                <Send className="w-4 h-4 mr-2" /> {testing ? "SENDEN…" : "TEST-E-MAIL"}
              </Button>
              <Button
                onClick={sendDigestNow}
                variant="outline"
                data-testid="send-digest-now"
                className="rounded-sm border-zinc-400 h-11 px-5"
              >
                <Calendar className="w-4 h-4 mr-2" /> WOCHEN-MAIL JETZT
              </Button>
            </div>

            <div className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
              <span className="font-semibold text-zinc-700">Trigger:</span> Eine Benachrichtigung wird einmalig
              gesendet, sobald der Bestand eines Artikels die <em>Mindestmenge</em> erreicht oder unterschreitet.
              Die Mindestmenge ist pro Artikel einstellbar — Standard ist <span className="mono">0</span>
              (Mail bei Bestand = 0). Erst wenn der Bestand wieder über der Mindestmenge liegt, wird beim nächsten
              Unterschreiten erneut benachrichtigt.
              <br /><br />
              <span className="font-semibold text-zinc-700">Wochen-Zusammenfassung:</span> Jeden Montag um 8:00 Uhr
              (Europe/Berlin) gehen alle Artikel im Hauptlager unter Mindestmenge als Sammelmail raus. Mit dem Button
              oben kannst du sie jetzt sofort versenden.
            </div>
          </>
        )}
      </div>

      {/* CSV Import / Export */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6" style={{ borderLeft: "4px solid #0EA5E9" }}>
        <div className="flex items-center gap-3 mb-5">
          <FileUp className="w-5 h-5" />
          <div>
            <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">DATEN</div>
            <div className="font-display text-lg uppercase mt-0.5">CSV-Import / Export</div>
          </div>
        </div>
        <p className="text-sm text-zinc-600 mb-4">
          Importiere oder exportiere die komplette Artikelliste als CSV (Trennzeichen <code>;</code>,
          UTF-8). Spalten: <code className="mono text-[11px]">name; description; ean; quantity;
          quantity_buffer; quantity_pickup; min_quantity; ek_price; vk_price; shelf_label;
          compartment; slot; location_text</code>. Beim Import wird per EAN abgeglichen (vorhanden = update,
          sonst neu).
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 h-11 rounded-sm font-semibold tracking-wide flex items-center cursor-pointer">
            <FileUp className="w-4 h-4 mr-2" />
            {importing ? "IMPORTIEREN…" : "CSV IMPORTIEREN"}
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={onImportFile}
              data-testid="csv-import-input"
              className="hidden"
            />
          </label>
          <Button
            onClick={exportCsv}
            variant="outline"
            data-testid="csv-export-button"
            className="rounded-sm border-zinc-400 h-11 px-5"
          >
            <Download className="w-4 h-4 mr-2" /> CSV EXPORTIEREN
          </Button>
        </div>
        {importResult && (
          <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-sm p-4 text-sm" data-testid="import-result">
            <div className="flex gap-6 mono text-xs">
              <div><span className="text-zinc-500">Neu:</span> <span className="font-bold text-green-700">{importResult.created}</span></div>
              <div><span className="text-zinc-500">Aktualisiert:</span> <span className="font-bold text-blue-700">{importResult.updated}</span></div>
              <div><span className="text-zinc-500">Übersprungen:</span> <span className="font-bold text-red-700">{importResult.skipped}</span></div>
            </div>
            {importResult.errors?.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-zinc-700 mb-1">Fehler:</div>
                <ul className="text-xs text-red-700 mono space-y-0.5">
                  {importResult.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>Zeile {e.line}: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Komplett-Backup (ZIP) */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6" style={{ borderLeft: "4px solid #18181b" }}>
        <div className="flex items-center gap-3 mb-5">
          <Archive className="w-5 h-5" />
          <div>
            <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">UMZUG / KATASTROPHEN-BACKUP</div>
            <div className="font-display text-lg uppercase mt-0.5">Komplettes Backup</div>
          </div>
        </div>
        <p className="text-sm text-zinc-600 mb-5">
          Ein <strong>komplettes ZIP-Backup</strong> enthält alle Artikel, Bewegungen, Regale, Projekte, Lieferanten,
          Bestellungen und Benutzer (ohne Passwörter). Ideal für den Umzug auf einen anderen Server oder als
          Sicherheitskopie.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            onClick={() => downloadFile("/export/backup.zip", "lager-backup.zip")}
            data-testid="backup-download-button"
            className="bg-black hover:bg-zinc-800 text-white rounded-sm h-11 justify-start px-5"
          >
            <Download className="w-4 h-4 mr-2" /> KOMPLETT-BACKUP HERUNTERLADEN (ZIP)
          </Button>

          <div className="flex items-stretch gap-2">
            <Select value={backupMode} onValueChange={setBackupMode}>
              <SelectTrigger className="h-11 rounded-sm border-zinc-400 w-44" data-testid="backup-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm">
                <SelectItem value="replace">Komplett ersetzen</SelectItem>
                <SelectItem value="merge">Daten zusammenführen</SelectItem>
              </SelectContent>
            </Select>
            <label className="bg-[#F97316] hover:bg-[#EA580C] text-white px-5 h-11 rounded-sm font-semibold tracking-wide flex items-center cursor-pointer flex-1 justify-center">
              <FileUp className="w-4 h-4 mr-2" />
              {backupImporting ? "IMPORTIEREN…" : "ZIP IMPORTIEREN"}
              <input
                type="file"
                accept=".zip,application/zip"
                onChange={onBackupImport}
                data-testid="backup-import-input"
                className="hidden"
              />
            </label>
          </div>
        </div>

        {backupMode === "replace" && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-sm p-3 flex items-start gap-2 text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <strong>Modus „Komplett ersetzen":</strong> Beim Import werden ALLE aktuellen Daten überschrieben.
              Lade vorher unbedingt ein aktuelles Backup herunter.
            </div>
          </div>
        )}

        {backupResult && (
          <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-sm p-4 text-sm" data-testid="backup-result">
            <div className="mono text-xs text-zinc-500 mb-2">
              Modus: <strong>{backupResult.summary?.mode}</strong>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mono">
              {Object.entries(backupResult.summary?.collections || {}).map(([coll, stats]) => (
                <div key={coll} className="bg-white border border-zinc-200 rounded-sm p-2">
                  <div className="text-zinc-500">{coll}</div>
                  <div className="font-bold mt-0.5">
                    {Object.entries(stats).map(([k, v]) => `${k}: ${v}`).join(" / ")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Einzelne CSV-Exports */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6" style={{ borderLeft: "4px solid #10B981" }}>
        <div className="flex items-center gap-3 mb-5">
          <Database className="w-5 h-5" />
          <div>
            <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">REPORTING</div>
            <div className="font-display text-lg uppercase mt-0.5">CSV-Exports (Excel)</div>
          </div>
        </div>
        <p className="text-sm text-zinc-600 mb-5">
          Einzelne Bereiche als CSV exportieren (UTF-8, Trennzeichen <code>;</code> — Excel-tauglich).
          Ideal für Buchhaltung, Inventur und Auswertungen.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={() => downloadFile("/export/stock-snapshot.csv", "lagerbestand.csv")}
            data-testid="export-stock-button"
            className="rounded-sm border-zinc-400 h-11 justify-start"
          >
            <Download className="w-4 h-4 mr-2" /> Lagerbestand + Werte (EK/VK)
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadFile("/export/movements.csv", "bewegungen.csv")}
            data-testid="export-movements-button"
            className="rounded-sm border-zinc-400 h-11 justify-start"
          >
            <Download className="w-4 h-4 mr-2" /> Bewegungen / Historie
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadFile("/export/projects.csv", "projekte.csv")}
            data-testid="export-projects-button"
            className="rounded-sm border-zinc-400 h-11 justify-start"
          >
            <Download className="w-4 h-4 mr-2" /> Projekte-Übersicht
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadFile("/export/suppliers.csv", "lieferanten.csv")}
            data-testid="export-suppliers-button"
            className="rounded-sm border-zinc-400 h-11 justify-start"
          >
            <Download className="w-4 h-4 mr-2" /> Lieferanten
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-5 h-5" />
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">DATENSCHUTZ</div>
        </div>
        <p className="text-sm text-zinc-600">
          Die Anwendung läuft komplett in eurem Mandanten. EANs werden lokal generiert (Präfix 200·,
          intern). Etiketten werden im Browser als PDF gerendert und nicht hochgeladen.
        </p>
      </div>      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <Server className="w-5 h-5" />
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-500">SYSTEM</div>
        </div>
        <div className="text-sm text-zinc-600 mono">
          Lagerverwaltung Handwerk · v1.2.0
        </div>
      </div>
    </div>
  );
}
